import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiRefreshCw } from "react-icons/fi";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import { nseIndicesOptions } from "../Helper/Helperdata";
import useLivePrices from "../../hooks/useLivePrices";
import { useMarketSnapshot } from "../../context/MarketSnapshotContext";
import { useIndexSymbols } from "../../context/IndexSymbolContext";
import { InfoTooltip } from "../Helper/InfoToolTip";
import { TOOLTIPS } from "../../helper/tooltilData";

const PROXIMITY_OPTIONS = [
  { label: "Breaking Out", value: 0 }, // LTP >= 52w high
  { label: "Within 2%", value: 2 },
  { label: "Within 5%", value: 5 },
];

const WeekHighBreakout = () => {
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(
    () => localStorage.getItem("52wIndex") || "NIFTY 50",
  );
  const [proximity, setProximity] = useState(2); // default: within 2%
  const [sortKey, setSortKey] = useState("distPct");
  const [sortDir, setSortDir] = useState("asc"); // closest first

  const { indexSymbols } = useIndexSymbols();
  const {
    loadDashboardIndexMarket,
    dashboardMarketData,
    resetDashboardMarketData,
  } = useMarketSnapshot();
  const { prices: livePrices, subscribeSymbols } = useLivePrices();
  const INDICES = [
    "NIFTY 50",
    "NIFTY BANK",
    "NIFTY IT",
    "NIFTY NEXT 50",
    "NIFTY MIDCAP 100",
  ];

  useEffect(() => {
    if (!selectedIndex) return;
    localStorage.setItem("52wIndex", selectedIndex);
    const symbols = indexSymbols?.[selectedIndex] || [];
    if (symbols.length) subscribeSymbols(symbols);
    loadDashboardIndexMarket({ indexName: selectedIndex });
  }, [selectedIndex, indexSymbols]);

  const handleIndexChange = (e) => {
    resetDashboardMarketData();
    setSelectedIndex(e.target.value);
  };

  const allStocks = useMemo(() => {
    const rows = Object.values(dashboardMarketData || {});
    return rows
      .map((item) => {
        const quote = item?.quote?.data;
        const live = livePrices[item?.symbol];
        if (!quote?.ohlc) return null;

        const ltp = live?.ltp ?? quote?.last_price;
        const close = quote?.ohlc?.close;
        const volume = live?.volume ?? quote?.volume;
        const averageVolume20 = item?.average_volume_20 || 0;

        // ── 52-week high ──
        // Adjust the field name to match YOUR API response
        const week52High =
          quote?.week_52_high ??
          quote?.yearHigh ??
          quote?.high_52_week ??
          item?.week_52_high ??
          null;

        if (!ltp || !week52High) return null;

        const pctChange = close
          ? Number((((ltp - close) / close) * 100).toFixed(2))
          : 0;

        // How far below the 52w high (negative = already broken out above)
        const distPct = Number(
          (((week52High - ltp) / week52High) * 100).toFixed(2),
        );

        const relativeVolume =
          Number.isFinite(volume) &&
          Number.isFinite(averageVolume20) &&
          averageVolume20 > 0
            ? Number((volume / averageVolume20).toFixed(2))
            : null;

        return {
          symbol: item.symbol,
          securityId: item.security_id,
          company: item.company || "",
          ltp,
          close,
          volume,
          averageVolume20,
          pctChange,
          week52High,
          distPct, // 0 or negative = breakout
          relativeVolume,
        };
      })
      .filter(Boolean);
  }, [dashboardMarketData, livePrices]);

  // ── Filter by proximity ──
  const filtered = useMemo(() => {
    if (proximity === 0) {
      // Only stocks at or above 52w high
      return allStocks.filter((s) => s.distPct <= 0);
    }
    return allStocks.filter((s) => s.distPct <= proximity);
  }, [allStocks, proximity]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = a[sortKey] ?? -Infinity;
      const bVal = b[sortKey] ?? -Infinity;
      if (typeof aVal === "string")
        return sortDir === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [filtered, sortKey, sortDir]);

  const SortTh = ({ label, field, align }) => (
    <th className={align === "left" ? "sm__th-left" : ""}>
      <div className="sm__sort-head">
        {label}
        <span className="sm__sort-arrows">
          <FaChevronUp
            size={10}
            className={sortKey === field && sortDir === "asc" ? "active" : ""}
            onClick={() => {
              setSortKey(field);
              setSortDir("asc");
            }}
          />
          <FaChevronDown
            size={10}
            className={sortKey === field && sortDir === "desc" ? "active" : ""}
            onClick={() => {
              setSortKey(field);
              setSortDir("desc");
            }}
          />
        </span>
      </div>
    </th>
  );

  const getDistClass = (dist) => {
    if (dist <= 0) return "sm__clr-strong-bull"; // breakout
    if (dist <= 1) return "sm__clr-bull"; // very close
    if (dist <= 3) return "sm__clr-mild-bull"; // approaching
    return "sm__clr-neutral";
  };

  return (
    <div className="sm">
      <div className="sm__header">
        <div className="sm__header-left">
          <h1 className="sm__title">52-Week High Breakout</h1>
          <p className="sm__sub">
            Stocks near or breaking above their 52-week high
          </p>
        </div>
        <div className="sm__header-right">
          <div className="sm__threshold-group">
            {PROXIMITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`sm__threshold-btn ${proximity === opt.value ? "sm__threshold-btn--active" : ""}`}
                onClick={() => setProximity(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <select
            className="sm__index-select"
            value={selectedIndex}
            onChange={handleIndexChange}
          >
            {nseIndicesOptions?.map((idx) => (
              <option key={idx} value={idx}>
                {idx}
              </option>
            ))}
          </select>
          <button
            className="sm__refresh-btn"
            onClick={() =>
              loadDashboardIndexMarket({ indexName: selectedIndex })
            }
          >
            <FiRefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="sm__result-bar">
        <span className="sm__result-count">
          <b>{sorted.length}</b> stocks{" "}
          {proximity === 0
            ? "breaking 52W high"
            : `within ${proximity}% of 52W high`}
        </span>
        <span className="sm__live-indicator">
          <span className="sm__live-dot" /> Live
        </span>
      </div>

      <div className="sm__table-wrap">
        <table className="sm__table option-table">
          <thead>
            <tr>
              <SortTh label="Name" field="symbol" align="left" />
              <SortTh label="LTP" field="ltp" />
              <SortTh label="Chg %" field="pctChange" />
              <SortTh label="52W High" field="week52High" />
              <SortTh label="Dist %" field="distPct" />
              <SortTh label="Volume" field="volume" />
              <SortTh label="Avg Vol (20d)" field="averageVolume20" />
              <SortTh label="Rel Vol" field="relativeVolume" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((stock) => (
              <tr
                key={stock.symbol}
                className="sm__row"
                onClick={() =>
                  navigate(`/stockdetail/${stock.symbol}/${stock.securityId}`)
                }
              >
                <td className="sm__td-name">
                  <span className="sm__symbol">{stock.symbol}</span>
                </td>
                <td className="sm__td-num">
                  ₹
                  {stock.ltp?.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td
                  className="sm__td-num"
                  style={{
                    color:
                      stock.pctChange > 0
                        ? "#249966"
                        : stock.pctChange < 0
                          ? "#f87171"
                          : "#64748b",
                  }}
                >
                  {stock.pctChange > 0 ? "+" : ""}
                  {stock.pctChange}%
                </td>
                <td className="sm__td-num">
                  ₹
                  {stock.week52High?.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td className={`sm__td-num ${getDistClass(stock.distPct)}`}>
                  {stock.distPct <= 0 ? (
                    <span className="sm__signal-badge sm__signal--strong">
                      BREAKOUT +{Math.abs(stock.distPct)}%
                    </span>
                  ) : (
                    `-${stock.distPct}%`
                  )}
                </td>
                <td className="sm__td-num">
                  {stock.volume?.toLocaleString("en-IN")}
                </td>
                <td className="sm__td-num">
                  {stock.averageVolume20 > 0
                    ? stock.averageVolume20.toLocaleString("en-IN")
                    : "—"}
                </td>
                <td className="sm__td-num">
                  {stock.relativeVolume !== null
                    ? `${stock.relativeVolume}x`
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WeekHighBreakout;
